<template>
  <div @click="selectedInputItem = null; selectedOutputItem = null">
    <breadcrumbs home="Distillation">
      <template v-slot:right>
        <v-menu offset-y v-if="company.permissions.includes('config.write')" :close-on-content-click="false" nudge-bottom="5" left max-width="500">
          <template v-slot:activator="{ on }">
            <v-icon small v-on="on">mdi-tune-variant</v-icon>
          </template>
          <production-distilling-preferences @update="preferences=$event"/>
        </v-menu>
      </template>
    </breadcrumbs>

    <v-card flat>
      <v-form v-model="valid" ref="form" :disabled="!edit">
        <v-container fluid class="pt-6 px-6 purtrak-scrollable">

          <v-overlay absolute :opacity="0.15" style="height:calc(100vh - 158px)"
                     v-if="(spirits.length===0 || allTanks.length===0 || allStills.length===0) && !loading && !distillationId">
            <div style="position:relative;top:-100px;width:400px" class="d-inline-block purple white--text text-body-1 pa-5">
              Before you can distill, you must have
              <template v-if="spirits.length===0">
                spirits defined.
                <div class="mt-10 float-right">
                  <v-btn :to="{name:'Spirits'}" color="primary" small>Spirits</v-btn>
                </div>
              </template>
              <template v-else-if="allTanks.length===0">
                tanks defined.
                <div class="mt-10 float-right">
                  <v-btn :to="{name:'EquipmentTanks'}" color="primary" small>Tanks</v-btn>
                </div>
              </template>
              <template v-else-if="allStills.length===0">
                a stll defined.
                <div class="mt-10 float-right">
                  <v-btn :to="{name:'EquipmentStills'}" color="primary" small>Stills</v-btn>
                </div>
              </template>
            </div>
          </v-overlay>

          <v-row dense>
            <v-col cols="6" sm="4" md="3">
              <date-picker  v-model="distillation.date" @change="refresh();forDateAndSite();refreshLot()"/>
            </v-col>
            <v-col cols="6" sm="3" md="2" v-if="preferences.distillDuration">
              <duration-picker label="Duration" v-model="distillation.duration" dense/>
            </v-col>
            <v-col cols="4" sm="2" v-if="preferences.distillIncomplete">
              <v-checkbox label="Completed" v-model="distillation.completed" dense class="mt-1" @change="validate()"/>
            </v-col>
            <v-col cols="4" sm="3" lg="2" :offset-md="3+(preferences.distillDuration?0:2)+(preferences.distillIncomplete?0:2)">
              <v-text-field label="#" v-model="distillation.number" :rules="[fieldRules.required,fieldRules.maxLength(20)]" outlined dense :counter="20"/>
            </v-col>
          </v-row>
          <v-row dense v-if="sites.length>1">
            <v-col cols="12" sm="6" md="4">
              <v-autocomplete v-model="distillation.siteId" outlined dense label="Site" :items="sites" item-text="name" item-value="id" @change="switchSite"/>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12" sm="6" md="4">
              <v-menu right max-width="300" nudge-bottom="12" :open-on-click="false" :close-on-click="false" :close-on-content-click="false" content-class="tour-arrow tour-arrow-left purple white--text pt-6 px-4" transition="slide-y-transition" :value="noProcesses">
                <template v-slot:activator="{on}">
                  <v-autocomplete v-model="distillation.processId" outlined dense label="Process" :items="processes" item-text="name" item-value="id"
                                  @change="selectProcess" v-on="on"/>
                </template>
                You can create processes for consistency and to save time.
                <v-btn plain class="text-none mt-4 white--text" :to="{name:'ProductionDistillingProcesses'}">Go to processes</v-btn>
                <div class="mt-1">
                  <v-btn plain class="white--text mt-4 float-right" @click.stop="noProcesses=false">Okay</v-btn>
                </div>
              </v-menu>
            </v-col>
            <v-col cols="12" sm="6" md="4" offset-md="4">
              <v-autocomplete dense outlined label="Still" v-model="distillation.stillId" :items="stills" item-value="id" item-text="name" no-data-text="No stills found" :rules="[stillNeeded]"/>
            </v-col>
          </v-row>

          <v-row v-if="process && process.description" dense>
            <v-col cols="12" lg="8" class="primary--text">
              <em>{{process.description}}</em>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <outlined-block v-model="distillation.inputs" :rules="[inputsNeeded]" :error-messages="inputsError" :disabled="!edit" ref="inputs">
                <v-data-table width="100%" calculate-widths disable-filtering disable-pagination disable-sort hide-default-footer
                              :items="distillation.inputs" no-data-text="At least one input item is required" item-key="key"
                              :headers="[{text:'',value:'add',width:'10%',align:'center',cellClass:'pa-0',divider:true},
                                          {text:'Input',value:'inputType',width:'37%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt-table-top'},
                                          {text:'Source',value:'inputSourceType',width:'37%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt-table-top'},
                                          {text:'Quantity',value:'quantity',width:'15%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt-table-top',align:'right'}]"  class="no-sort"
                              :item-class="item => item.key=== selectedInputItem ? ($vuetify.theme.dark ? 'grey darken-1' : 'grey lighten-4') : ''"
                              @click.native.stop=""
                              @click:row="item => {selectedInputItem = item.key; $nextTick(() => this.$refs.form.validate())}"
                              ref="inputTableBody">
                  <template v-slot:item.add="{item,index}">
                    <v-icon :disabled="!edit" class="handle mt-n2">mdi-drag</v-icon>
                    <v-btn :disabled="item.static || !edit" class="mt-n2" icon @click.stop="deleteInput(index)" x-small><v-icon>mdi-delete</v-icon></v-btn>
                  </template>
                  <template v-slot:item.inputType="{item}">
                    <multi-level-select v-if="selectedInputItem===item.key" :menuData="inputTypes" :first.sync="item.type" :second.sync="item.id"  :disabled="item.static" @change="inputChangeType(item, item.id)"
                                        outlined label="Input"/>
                    <div v-else class="ml-1" :class="{'error--text':!item.type}">{{itemDescription(item)}}</div>
                  </template>
                  <template v-slot:item.inputSourceType="{item}">
                    <template v-if="selectedInputItem===item.key">
                      <div v-if="['Spirit','Wine','NonExcise'].includes(item.type)">
                        <vessel-select
                                    :tanks="tanks" :bulkContainers="bulkContainers" :vessel.sync="item.vesselType" :id.sync="item.vesselId"
                                    :hint="distillationId===undefined" outlined label="Vessel"
                                    :highlight="vessel => distillationId === undefined && vessel.contents.alcoholId === item.id"/>
                      </div>
                      <v-autocomplete v-else-if="item.type === 'Mash' && fermenters.length!==0" dense v-model="item.vesselId"
                                      class="text-body-2"
                                      :items="fermenters" item-text="name" item-value="id" :rules="[required]" label="Fermenter"
                                      :hint="fermenterVolume(item.vesselId)" persistent-hint outlined/>
                      <v-combobox v-else-if="item.type === 'Item' && inputItem(item).lotTracking" :items="currentLots(item)" v-model="item.lot" dense :rules="[lotValid,fieldRules.maxLength(20)]" class="text-body-2"
                                  @change="changeItemLot(item)" outlined label="Lot"/>
                      <v-btn v-if="['Spirit','Wine','NonExcise','Mash'].includes(item.type) && item.vesselId" x-small text @click="allContents(item)">
                        Use all contents
                      </v-btn>
                    </template>
                    <template v-else>
                      <div v-if="(item.type === 'Spirit' || item.type === 'Wine' || item.type === 'NonExcise')" class="ml-1" :class="{'error--text':!item.vesselId && distillation.completed}">
                        {{vesselName(item)}}
                      </div>
                      <div v-else-if="item.type === 'Mash'" class="ml-1" :class="{'error--text':!item.vesselId && distillation.completed}">
                        {{getFermenter(item.vesselId).name}}
                      </div>
                      <div v-else-if="item.type === 'Item' && inputItem(item).lotTracking" class="ml-1" :class="{'error--text':lotValid(item.lot)!==true}">
                        Lot: {{lotValid(item.lot)===true ? item.lot : 'MISSING'}}
                      </div>
                    </template>
                  </template>
                  <template v-slot:item.quantity="{item}">
                    <template v-if="selectedInputItem===item.key">
                      <decimal-number-input v-if="item.type === 'Item' || item.type === 'Mash'" label="Quantity" v-model="item.quantity"
                              prefix="" :suffix="quantitySuffix(item)" v-show="item.valid"
                              :precision="precision(item)" class="text-body-2"
                              :hint="distillationId?'':inputItem(item).quantity" persistent-hint/>
                      <alcohol-input v-else :spirit="quantitySuffix(item) === 'LAA'" :disabled="!edit" v-show="item.valid" class="text-body-2"
                                      :tare="getTank(item.vesselId).tare"
                                      label="Quantity"
                                      v-model="item.quantity" :litres.sync="item.litres" :kg.sync="item.kg" :strength.sync="item.strength"
                                      @change="alcoholInputChanged(item)"/>
                    </template>
                    <div v-else class="mr-1" :class="{'error--text':item.quantity<0}">
                      {{item.quantity | number(item.type === 'Item' || item.type === 'Mash' ? precision(item) : 3)}} {{quantitySuffix(item)}}
                      <template v-if="item.type === 'Spirit'">
                        <div v-if="company.settings.trackLitres">{{item.litres | number(3)}} litres</div>
                        <div v-if="company.settings.trackKg">{{item.kg | number(3)}} kg</div>
                        <div v-if="company.settings.trackStrength">{{item.strength | number(2)}}% alc./vol.</div>
                      </template>
                    </div>
                  </template>
                  <template v-slot:footer="{widths}">
                    <v-divider/>
                    <div @click="selectedInputItem = null" style="width:100%" class="py-2 px-0">
                      <div :style="'width:'+widths[0]+'px'" class="text-center">
                        <v-btn :disabled="!edit" fab color="primary" x-small @click.stop="addInput"><v-icon small>mdi-plus</v-icon></v-btn>
                      </div>
                    </div>
                  </template>
                </v-data-table>
              </outlined-block>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <outlined-block v-model="distillation.outputs" :rules="[outputsNeeded]" :disabled="!edit">
                <v-data-table width="100%" calculate-widths disable-filtering disable-pagination disable-sort hide-default-footer
                              :items="distillation.outputs" item-key="key" no-data-text="At least one output is required"
                              :headers="headers" class="no-sort"
                              :item-class="item => item.key=== selectedOutputItem ? ($vuetify.theme.dark ? 'grey darken-1' : 'grey lighten-4') : ''"
                              @click.native.stop=""
                              @click:row="item => {selectedOutputItem = item.key; $nextTick(() => this.$refs.form.validate())}"
                              ref="outputTableBody">
                  <template v-slot:item.add="{index,item}">
                    <v-icon :disabled="!edit" class="handle mt-n2">mdi-drag</v-icon>
                    <v-btn :disabled="item.static || !edit" class="mt-n2" icon @click.stop="deleteOutput(index)" x-small><v-icon>mdi-delete</v-icon></v-btn>
                  </template>
                  <template v-slot:item.type="{item}">
                    <multi-level-select v-if="selectedOutputItem===item.key" :menuData="outputTypes" :first.sync="item.type" :second.sync="item.spiritId" :disabled="item.static" outlined label="Output"/>
                    <div v-else class="ml-1" :class="{'error--text':!item.spiritId}">{{itemDescription(item)}}</div>
                  </template>
                  <template v-slot:item.tank="{item}">
                    <tank-select v-if="selectedOutputItem===item.key" v-model="item.tankId" :tanks="validOutputTanks(item.tankId)" :required="distillation.completed"
                                 :highlight="tank => distillationId === undefined && (tank.contents.alcoholId === item.spiritId || tank.contents.alcoholId === null)"
                                 :hint="distillationId===undefined"
                                 outlined label="Tank"/>
                    <div v-else class="ml-1" :class="{'error--text':!item.tankId && distillation.completed}">
                      {{getTank(item.tankId).name}}
                    </div>
                  </template>
                  <template v-slot:item.lot="{item}">
                    <v-text-field v-if="selectedOutputItem===item.key" dense v-model="item.lot" class="text-body-2" :rules="[required]" outlined label="Lot"/>
                    <div v-else class="ml-1" :class="{'error--text':!item.lot && distillation.completed}">{{item.lot || 'REQUIRED'}}</div>
                  </template>
                  <template v-slot:item.quantity="{item}">
                    <template v-if="selectedOutputItem===item.key">
                      <decimal-number-input v-if="item.type==='NonExcise'" label="Quantity" prefix="" suffix="litres" :precision="3" v-model="item.quantity"/>
                      <alcohol-input  v-else spirit class="text-body-2"
                                      :tare="getTank(item.tankId).tare"
                                      label="Quantity"
                                      v-model="item.quantity" :litres.sync="item.litres" :kg.sync="item.kg" :strength.sync="item.strength"/>
                    </template>
                    <div v-else class="mr-1" :class="{'error--text':item.quantity<0}">
                      {{item.quantity | number(3)}} {{item.type==='NonExcise'?'litres':'LAA'}}
                      <template v-if="item.type !== 'NonExcise'">
                        <div v-if="company.settings.trackLitres">{{item.litres | number(3)}} litres</div>
                        <div v-if="company.settings.trackKg">{{item.kg | number(3)}} kg</div>
                        <div v-if="company.settings.trackStrength">{{item.strength | number(2)}}% alc./vol.</div>
                      </template>
                    </div>
                  </template>
                  <template v-slot:item.carry="{item}">
                    <template>
                      <v-simple-checkbox v-if="selectedOutputItem===item.key" v-model="item.carryValue" :disabled="item.static" class="mt-n5"/>
                      <v-icon v-else-if="item.carryValue">mdi-check</v-icon>
                    </template>
                  </template>
                  <template v-slot:footer="{widths}">
                    <v-divider/>
                    <div @click="selectedOutputItem = null" style="width:100%" class="py-2 px-0">
                      <div :style="'width:'+widths[0]+'px'" class="text-center">
                        <v-btn fab :disabled="!edit" color="primary" x-small @click.stop="addOutput">
                          <v-icon small>mdi-plus</v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </template>
                </v-data-table>
              </outlined-block>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-textarea label="Comments" rows="3"
                           v-model="distillation.comments" outlined dense :rules="[fieldRules.maxLength(500)]" counter="500"/>
            </v-col>
          </v-row>
        </v-container>
      </v-form>

      <v-divider/>
      <v-card-actions class="px-6">
        <template v-if="distillationId && company.permissions.includes('production.write')">
          <v-switch v-if="distillation.date > company.excisePeriodEnd || edit" :disabled="dirty"
                    v-model="edit" label="Edit" hide-details class="mt-0" @change="forDateAndSite()"/>
          <div v-else class="grey--text">Cannot edit since excise filed to period end {{company.excisePeriodEnd | formatDate}}</div>
        </template>
        <v-spacer/>
        <v-btn :plain="edit" color="primary" @click="cancel">{{edit?'Cancel':'Done'}}</v-btn>
        <v-btn v-if="edit" color="primary" :loading="saving" @click="save">Save</v-btn>
      </v-card-actions>
    </v-card>

    <confirm-leave v-model="next"/>
  </div>
</template>

<style>
  .pt-table-top {vertical-align: top;padding-bottom:10px !important}
</style>

<script>
import {format} from 'date-fns';
import sortableJS from 'sortablejs';
import confirmLeave from '@/mixins/confirmLeave'
import TankSelect from '@/components/TankSelect'
import BulkContainerSelect from '@/components/BulkContainerSelect'
import VesselSelect from '@/components/VesselSelect'
import ProductionDistillingPreferences from '@/views/production/distilling/ProductionDistillingPreferences'
import createHierarchyList from '@/mixins/createHierarchyList'

export default {
  name: 'ProductionDistillingEdit',
  components: {TankSelect,BulkContainerSelect,ProductionDistillingPreferences,VesselSelect},
  mixins: [confirmLeave,createHierarchyList],
  props: {
    distillationId: {type:String, required:false},
    processId: {type:String, required:false},
    activityId: {type:String, required:false}
  },
  data () {
    return {
      edit: false,
      distillation: {date:format(new Date(), 'yyyy-MM-dd HH:mm:ss'),number:'',siteId:this.$store.getters.sites(true, {B266:true})[0].id,stillId:'',processId:'',
                     completed:true,duration:0,comments:'',inputs:[],outputs:[],activityId:this.activityId},
      preferences: {},
      processes: [{id:null,name:'AD HOC (no process)'},{divider:true}],
      noProcesses: false,
      allStills: [],
      spirits: [],
      wines: [],
      categories: [],
      nonExcises: [],
      allTanks: [],
      allBulkContainers: [],
      items: [],
      allFermenters: [],
      baseLot: '',

      inputsError: '',

      dirty: false,
      saving: false,
      loading: true,
      selectedInputItem: null,
      selectedOutputItem: null,
      valid: true,
      required: v => !this.distillation.completed || !!v || 'Required',
      inputsNeeded: v => !this.distillation.completed || v.length > 0 || 'Distillation inputs needed',
      outputsNeeded: v => !this.distillation.completed || v.length > 0 || 'Distillation outputs needed',
      stillNeeded: () =>  this.stills.length>0 || 'A still must be specified',
      lotValid: lot => !this.distillation.completed || !!lot || 'Lot is mandatory',
      sortableInput: null,
      sortableOutput: null
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    stills() {
      return this.allStills.filter(still => still.site.id === this.distillation.siteId);
    },
    tanks() {
      return this.allTanks.filter(tank => tank.site.id === this.distillation.siteId);
    },
    bulkContainers() {
      return this.allBulkContainers.filter(bulkContainer => bulkContainer.contents.siteId === this.distillation.siteId);
    },
    fermenters() {
      return this.allFermenters.filter(fermenter => fermenter.site.id === this.distillation.siteId);
    },
    adhoc() {
      return !this.distillation.processId;
    },
    sites() {
      return this.$store.getters.sites(!this.distillationId, {B266:true});
    },
    process() {
      return this.processes.find(process => process.id === this.distillation.processId);
    },
    inputTypes() {
      const types = [];
      if (this.spirits.length)
        types.push({name:'Spirit',id:'Spirit',menu:this.spirits});
      types.push({name:'Mash',id:'Mash'});
      if (this.wines.length)
        types.push({name:'Wine',id:'Wine',menu:this.wines});
      if (this.nonExcises.length)
        types.push({name:'Non-excise',id:'NonExcise',menu:this.nonExcises});
      if (this.items.length) {
        if (this.categories.length) {
          const categories = this.createHierarchyList(this.categories);
          for (const category of categories) {
            const sub = this.items.filter(item => item.category?.id === category.id);
            if (sub.length)
              category.menu = sub;
            else
              category.disabled = true;
          }
//          types.push({name:'Item',id:'Item',menu:[...categories.filter(category => !category.disabled)]});
          types.push({name:'Item',id:'Item',menu:categories});
          const leftOver = this.items.filter(item => !item.category?.id);
          if (leftOver.length)
            types[types.length-1].menu.push({divider:true},{name:'Other items',id:'x',menu:leftOver});
        }
        else
          types.push({name:'Item',id:'Item',menu:this.items});
      }
      return types;
    },
    outputTypes() {
      const types = [];
      types.push({name:'Spirit',id:'Spirit',menu:this.spirits});
      if (this.nonExcises.length)
        types.push({name:'Non-excise',id:'NonExcise',menu:this.nonExcises});
      return types;
    },
    headers() {
      const headers = [{text:'',value:'add',width:'10%',align:'center',cellClass:'pa-0',divider:true},
                      {text:'Output',value:'type',width:this.preferences.distillLot?'37%':'42%',divider:true,class:'px-2',cellClass:'py-2 px-1'},
                      {text:'Tank',value:'tank',width:this.preferences.distillLot?'18%':'23%',divider:true,class:'px-2',cellClass:'py-2 px-1'}];
      if (this.preferences.distillLot)
        headers.push({text:'Lot',value:'lot',width:'10%',divider:true,class:'px-2',cellClass:'py-2 px-1'});
      return [...headers, {text:'Quantity',value:'quantity',width:'15%',divider:true,class:'px-2',cellClass:'py-2 px-1',align:'right'},
                          {text:'Carry value?',value:'carry',width:'10%',align:'center',divider:true,class:'px-2',cellClass:'py-2 px-1'}];
    }
  },
  watch: {
    distillation: {
      handler() {
        this.dirty = true;
      },
      immediate: true,
      deep: true
    },
    adhoc() {
      if (this.inputsIconSort)
        this.inputsIconSort.option('disabled', !this.adhoc);
    },
    edit() {
      if (this.inputIconSort)
        this.inputIconSort.option('disabled', !this.edit);
      if (this.outputIconSort)
        this.outputIconSort.option('disabled', !this.edit);
    }
  },
  async mounted() {
    await Promise.all([
      this.$lazyListLoad(this.processes, 'processes2', 'id,name,scaleToId,description', 100, this.distillationId ? [] : [{field:'active', comparator:'eq', value:'true'}]),
      this.$lazyListLoad(this.allStills, 'stills2', 'id,name,site{id}', 100, this.distillationId?[]:[{field:'active', comparator:'eq', value:'true'}]),
      this.$lazyListLoad(this.categories, 'categories2', 'id,name,parent{id}', 100, [], [{name:'type',value:'Item'}]),
      this.$lazyListLoad(this.items, 'items2', 'id,name,trackUnits,allowNegativeInventory,type,lotTracking,precision,category{id}', 100, this.distillationId?[]:[{field:'active', comparator:'eq', value:'true'}]),
      this.$lazyListLoad(this.spirits, 'spirits2', 'id,name', 100, this.distillationId ? [] : [{field:'active', comparator:'eq', value:'true'}]),
      this.company.allowWine ?  this.$lazyListLoad(this.wines, 'wines2', 'id,name', 100, this.distillationId ? [] : [{field:'active', comparator:'eq', value:'true'}]) : [],
      this.$lazyListLoad(this.nonExcises, 'nonExcises2', 'id,name', 100, this.distillationId ? [] : [{field:'active', comparator:'eq', value:'true'}])
    ]);
    const results = await this.$apiCall(`query ProductionDistillingEdit($edit:Boolean!, $id:ID!) {
                                              distillation(id:$id) @include(if:$edit) {
                                                id,processId,date,number,siteId,stillId,completed,duration,comments,created,
                                                inputs {type,id,quantity,litres,kg,strength,vesselType,vesselId,lot},
                                                outputs {spiritId,tankId,carryValue,quantity,litres,kg,strength,lot,nonExcise}
                                              }
                                              distillingPreferences {
                                                distillDuration,distillIncomplete,distillLot
                                              }
                                            }`,
                                        {edit:this.distillationId!=undefined,
                                         id:this.distillationId || '',
                                         active:this.distillationId?null:true});
    if (results.error) {
      this.$nextTick(() => this.dirty = false);
      this.$router.back();
      return;
    }

    this.preferences = results.data.distillingPreferences;

    this.switchSite();

    if (this.distillationId) {
      this.$lazyListLoad(this.allTanks, 'tanks2', 'id,name,tare,site{id}', 100);
      this.$lazyListLoad(this.allFermenters, 'fermenters2', 'id,name,site{id}', 100);
      this.$lazyListLoad(this.allBulkContainers, 'bulkContainers2', 'id,name,contents{siteId}', 100);
      this.distillation = results.data.distillation;
      this.edit = !this.distillation.completed;
      if (!this.distillation.completed)
        this.distillation.duration = this.$roundNumber(Date.now()/60000 - this.distillation.created, 0);
      let key = Date.now();
      this.distillation.inputs.forEach(input => {input.valid = true; input.key = key++});
      this.distillation.outputs.forEach(output => {output.type = output.nonExcise ? 'NonExcise':'Spirit', output.key = key++});
      this.selectProcess(false);
    }
    else {
      const results = await this.$apiCall(`query ProductionDistillingEdit {
                                              distillationNextNumber
                                            }`);
      if (!results.error)
        this.distillation.number = results.data.distillationNextNumber;
      await this.refresh();
      if (this.processes.length) {
        this.distillation.processId = this.processId || this.processes[0].id;
        await this.selectProcess();
      }
      this.noProcesses = this.processes.length === 2 && !this.$vuetify.breakpoint.mobile && this.spirits.length !== 0 && this.allTanks.length !== 0 && this.allStills.length !== 0;
      this.edit = true;
    }

    await this.refreshLot();
    this.$nextTick(() => {
      this.dirty = false
      this.$refs.form.validate();
    });

    setTimeout(() => this.loading = false, 1_000);

    const _self = this;

    this.sortableInput = sortableJS.create(this.$refs.inputTableBody.$el.querySelector('tbody'), {
      handle: '.handle',
      onEnd({newIndex, oldIndex}) {
        const rowSelected = _self.distillation.inputs.splice(oldIndex, 1)[0];
        _self.distillation.inputs.splice(newIndex, 0, rowSelected);
      }
    })
    this.sortableOutput = sortableJS.create(this.$refs.outputTableBody.$el.querySelector('tbody'), {
      handle: '.handle',
      onEnd({newIndex, oldIndex}) {
        const rowSelected = _self.distillation.outputs.splice(oldIndex, 1)[0];
        _self.distillation.outputs.splice(newIndex, 0, rowSelected);
      }
    })
  },
  beforeDestroy() {
    this.sortableInput.destroy();
    this.sortableOutput.destroy();
  },

  methods: {
    allContents(item) {
      const vesselsList = item.type === 'Mash' ? this.fermenters : {'Tank':this.tanks,'BulkContainer':this.bulkContainers}[item.vesselType];
      const vessel = vesselsList.find(v => v.id === item.vesselId);
      if (vessel) {
        item.quantity = vessel.contents.volume;
        if (item.type !== 'Mash') {
          item.litres = vessel.contents.litres;
          item.kg = vessel.contents.kg;
          item.strength = vessel.contents.strength;
        }
      }
    },
    itemDescription(item) {
      if (!item.type)
        return 'MISSING';
      if (item.type === 'Mash')
        return 'Mash';
      const lists = {'Spirit':this.spirits,'Wine':this.wines,'NonExcise':this.nonExcises,'Item':this.items};
      return `${item.type}: ${lists[item.type]?.find(i => i.id === item.id || i.id === item.spiritId)?.name}`;
    },
    vesselName(item) {
      return {'Tank':this.tanks,'BulkContainer':this.bulkContainers}[item.vesselType]?.find(v => v.id === item.vesselId)?.name ?? 'MISSING';
    },
    async refresh() {
      if (this.distillationId)
        return;

      this.allFermenters = [];
      this.allTanks = [];
      this.allBulkContainers = [];
      this.$lazyListLoad(this.allFermenters, 'fermenters2', `id,name,site{id},contents(date:"${this.distillation.date}") {volume}`, 100, [{field:'active', comparator:'eq', value:'true'}]);
      this.$lazyListLoad(this.allTanks, 'tanks2', `id,name,tare,site{id},contents{volume,alcoholType,litres,kg,strength,alcoholName,alcoholId}`, 100, [{field:'active', comparator:'eq', value:'true'}], [{name:'asof', value:this.distillation.date}]);
      this.$lazyListLoad(this.allBulkContainers, 'bulkContainers2', `id,name,contents{siteId,volume,alcoholType,litres,kg,strength,alcoholId,alcoholName}`, 100, [{field:'contents.volume', comparator:'ne', value:'0'}], [{name:'asof', value:this.distillation.date}]);
    },
    getFermenter(fermenterId) {
      return this.fermenters.find(fermenter => fermenter.id === fermenterId) ?? {name:'FERMENTER MISSING', volume:0, contents:{volume:0}};
    },
    getTank(tankId) {
      return this.tanks.find(vessel => vessel.id === tankId) ?? {tare:0, name:'MISSING'};
    },
    inputContents(input) {
      if (this.distillationId)
        return;
      const vessel = (input.vesselType === 'Tank' ? this.tanks : this.bulkContainers).find(vessel => vessel.id === input.vesselId);
      return vessel?.contents.volume ? vessel.contents.alcoholName : '(empty)';
    },
    fermenterVolume(fermenterId) {
      if (this.distillationId)
        return;
      return `${this.$number(this.getFermenter(fermenterId).contents.volume)} litres`;
    },
    validate() {
      setTimeout(() => this.$refs.form.validate());
    },
    quantitySuffix(input) {
      const suffixes = {Spirit:'LAA',Mash:'litres',Wine:'litres','NonExcise':'litres'};
      if (input.type === 'Item' && this.items.length > 0)
        return this.inputItem(input).trackUnits;
      return suffixes[input.type];
    },
    precision(input) {
      if (input.type === 'Item' && this.items.length > 0)
        return this.inputItem(input).precision;
      return 0;
    },
    addInput() {
      const input = {type:'',key:Date.now(),valid:true,vesselType:null,quantity:0,litres:0,kg:0,strength:0,id:'',vesselId:'',lot:'',errorMessage:null};   // key is needed to sort properly
      this.inputChangeType(input);
      this.distillation.inputs.push(input);
      this.selectedInputItem = input.key;
      this.validate();
    },
    deleteInput(index) {
      // If from a process, can only delete input if it's a duplicate
      if (!this.adhoc) {
        const inputToDelete = this.distillation.inputs[index];
        if (inputToDelete.static && !this.distillation.inputs.some(input => input.key !== inputToDelete.key &&
                                                                            input.type === inputToDelete.type &&
                                                                            input.id === inputToDelete.id))
          return;
      }

      this.distillation.inputs.splice(index, 1);
    },
    // eslint-disable-next-line no-unused-vars
    inputChangeType(input, id='', quantity=0) {
      input.valid = true;
      input.vesselType = null;
      input.quantity = quantity || 0;
      input.id = id;
      input.vesselId = '';
      input.errorMessage = null;
      if (input.type === 'Spirit')
        this.inputChangeAlcohol(input);
      else if (input.type === 'Mash') {
        input.valid = this.fermenters.length > 0;
        this.validate();
      }
      else if (input.type === 'Wine')
        this.inputChangeAlcohol(input);
      else if (input.type === 'NonExcise')
        this.inputChangeAlcohol(input);
      else if (input.type === 'Item')
        this.inputChangeItem(input);
    },
    inputChangeAlcohol(input) {
      input.valid = true;
      if (!this.vesselsForInput().some(v => v.value === input.vesselType)) {
        input.vesselType = this.vesselsForInput()[0].value;
        this.inputChangeVesselType(input);
      }
      this.validate();
    },
    async forDateAndSite() {
//      this.loading = true;
      for (const input of this.distillation.inputs) {
        if (input.type === 'Item') {
          const item = this.items.find(item => item.id === input.id);
          if (!item)
            continue;

          // Get proper quantity and lot info for current date and site
          delete item.sites;
          await this.inputChangeItem(input);

          // Make sure lot is still valid, unless this is an edit where we'll assume it must be valid
          if (!this.distillationId && !this.currentLots(input).some(lot => lot === input.lot)) {
            input.lot = '';
            item.quantity = '';
          }
        }
      }
//      this.loading = false;
    },
    async inputChangeItem(input) {
      const item = this.inputItem(input);
      if (item.id && !item.sites) {
        const results = await this.$apiCall(`query ProductionDistillingEdit($id:ID!, $date:DateTime!) {
                                                  item(id:$id) {
                                                    sites(asof:$date) {siteId,lot,quantity}
                                                  }
                                                }`,
                                            {id:item.id,date:this.distillation.date});
        if (!results.error)
          this.$set(item, 'sites', results.data.item.sites);
      }
      this.validate();
    },
    currentLots(input) {
      const item = this.inputItem(input);
      return item && item.sites ?
                                item.sites.filter(site => site.siteId === this.distillation.siteId).map(site => site.lot)
                                : [];

    },
    changeItemLot(input) {
      const item = this.inputItem(input);
      const site = item.sites && item.sites.find(site => site.siteId === this.distillation.siteId && site.lot === input.lot);
      item.quantity = `${site ? this.$number(site.quantity, item.precision) : '0'} ${item.trackUnits}`;
    },
    inputChangeVesselType(input) {
//      if (input.vesselType === 'Tank' && this.tanks.some(t => t.id === input.vesselId) ||
//          input.vesselType === 'BulkContainer' && this.bulkContainers.some(b => b.id === input.vesselId))
//        return;
      input.vesselId = '';
      this.validate();
    },
    vesselsForInput() {
      const vessels = [{text:'Tank',value:'Tank'}];
      if (this.bulkContainers.length > 0)
        vessels.push({text:'Bulk',value:'BulkContainer'});
      return vessels;
    },
    inputItem(input) {
      return this.items.find(i => i.id === input.id) || {};
    },
    addOutput() {
      const output = {spiritId: this.adhoc ? '' : this.distillation.outputs[0].spiritId,
                      type: this.adhoc ? '' : this.distillation.outputs[0].type,
                      tankId:'',carryValue:this.distillation.outputs.length===0,
                      quantity:0,litres:0,kg:0,strength:0,key:Date.now()};   // key is needed to sort properly
      this.setOutputLot(output);
      this.distillation.outputs.push(output);
      this.selectedOutputItem = output.key;
      this.validate();
    },
    deleteOutput(index) {
      this.distillation.outputs.splice(index, 1);
    },
    validOutputTanks(includeId) {
      return this.tanks.filter(tank =>
        tank.id === includeId || !this.distillation.outputs.some(o => o.tankId === tank.id)
      );
    },

    switchSite() {
      if (!this.stills.find(still => still.id === this.distillation.stillId))
        this.distillation.stillId = this.stills.length !== 0 ? this.stills[0].id : '';
      this.distillation.inputs.forEach(input => {
        this.inputChangeType(input, input.id, input.quantity)
      });
      this.forDateAndSite();
      this.distillation.outputs.forEach(output => {
        output.tankId = this.tanks.length ? this.tanks[0].id : '';
      });
    },

    async selectProcess(reset=true) {
      if (this.adhoc) {
        this.distillation.inputs.forEach(input => delete input.static);
        this.distillation.outputs.forEach(output => delete output.static);
        return;
      }
      const process = this.processes.find(p => p.id ===this.distillation.processId);
      let preferredStillId;
      if (!process.inputs) {
//        this.loading = true;
        const results = await this.$apiCall(`query ProductionDistillingEdit($id:ID!) {
                                                  process(id:$id) {
                                                    preferredStillId
                                                    inputs{type,id,quantity,minQuantity,maxQuantity,precision}
                                                    outputs{spiritId,carryValue,nonExcise}
                                                  }
                                                }`,
                                            {id:process.id});
        if (!results.error) {
          preferredStillId = results.data.process.preferredStillId;
          this.$set(process, 'inputs', results.data.process.inputs);
          this.$set(process, 'outputs', results.data.process.outputs);
          this.validate();
        }
//        this.loading = false;
      }

      if (reset) {    // Not done when editing existing distillation
        this.distillation.stillId = preferredStillId || this.stills[0]?.id  || '';
        let key = Date.now();
        this.distillation.inputs.splice(0, this.distillation.inputs.length);
        process.inputs.forEach(processInput => {      // Need to do it this way to be reactive
          const input = {type:processInput.type, key:key++, static:true};
          this.inputChangeType(input, processInput.id, processInput.quantity);
          this.distillation.inputs.push(input);
        });
        this.distillation.outputs.splice(0, this.distillation.outputs.length);
        process.outputs.forEach(processOutput => {      // Need to do it this way to be reactive
          const output = {spiritId:processOutput.spiritId, tankId:'' /*tanks.length ? tanks[0].id : null*/,
                          carryValue:processOutput.carryValue,quantity:0,key:key++,static:true,type:processOutput.nonExcise?'NonExcise':'Spirit'};
          this.setOutputLot(output);
          this.distillation.outputs.push(output);
        });
      }
    },

    alcoholInputChanged(input) {
      if (this.adhoc)
        return;

      const process = this.processes.find(p => p.id === this.distillation.processId);
      if (!process.scaleToId)
        return;

      // Scale factor
      let scaleFactor = 1;
      const base = process.inputs.find(input => input.id === process.scaleToId).quantity;
      scaleFactor = base ? input.quantity / base : 0;

      // Adjust all items
      this.distillation.inputs.forEach(input => {
        if (input.type === 'Item') {
          const processInput = process.inputs.find(pi => pi.id === input.id);
          if (processInput)
            input.quantity = this.$roundNumber(processInput.quantity * scaleFactor, processInput.precision);
        }
      });
    },

    setOutputLot(output) {
      let seq = 1;
      while (this.distillation.outputs.some(o => o !== output && o.lot === `${this.baseLot}-${seq}`))
        seq++;
      output.lot = `${this.baseLot}-${seq}`;
    },
    async refreshLot() {
      if (this.distillationId)    // Don't do for editing existing distillations
        return;
//      this.loading = true;
      const results = await this.$apiCall(`query ProductionDistillingEdit($asof:DateTime!) {
                                            lotUnique(prefix:"S",date:$asof)
                                          }`,
                                          {asof:this.distillation.date});
      if (!results.error) {
        this.baseLot = results.data.lotUnique;
        this.distillation.outputs.forEach(output => this.setOutputLot(output));
      }

//      this.loading = false;
    },
    cancel() {
      this.$router.back();
    },
    async save() {
      const incomplete = this.distillation.inputs.some(item => !item.type ||
                                                              (item.type === 'Spirit' || item.type === 'Wine' || item.type === 'NonExcise') && this.distillation.completed && !item.vesselId ||
                                                              item.type === 'Mash' && this.distillation.completed && !item.vesselId ||
                                                              item.type === 'Item' && this.inputItem(item).lotTracking && this.lotValid(item.lot)!==true ||
                                                              item.quantity < 0) ||
                         this.distillation.outputs.some(item => !item.spiritId ||
                                                                !item.tankId && this.distillation.completed ||
                                                                (this.preferences.distillLot && !item.lot && this.distillation.completed) ||
                                                                item.quantity < 0);
      if (!this.valid ||incomplete) {
        this.$store.commit('setError', {type:'warning',message:`One or more fields is missing or invalid`});
        return;
      }
      this.saving = true;
      // eslint-disable-next-line no-unused-vars
      const {inputs, outputs,created, ...input} = this.distillation;
      if (this.adhoc)
        delete input.processId;
      input.inputs = this.distillation.inputs.map(input =>
        ({type:input.type,
          id:input.id, quantity:input.quantity,litres:input.litres,kg:input.kg,strength:input.strength,vesselType:input.vesselType,vesselId:input.vesselId,lot:input.lot})
      );
      input.outputs = this.distillation.outputs.map(output =>
        ({spiritId:output.spiritId,carryValue:output.carryValue,tankId:output.tankId,quantity:output.quantity,litres:output.litres,kg:output.kg,strength:output.strength,lot:output.lot})
      );

      const results = await this.$apiCall(`mutation ProductionDistillingEdit($input: DistillationInput!) {
                                             distillationCreate(input:$input) {id}
                                           }`,
                                          {input})
      this.saving = false;
      if (!results.error) {
        this.dirty = false;
        this.$router.back();
        this.$store.commit('setError', {type:'success',message:`${this.distillation.id?'Updated':'Created'} distillation`});
        if (this.activityId)
          this.$store.dispatch('checkTasks', true);
      }
    }
  }
}
</script>